import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTasks, createTask, updateTask, moveTask, deleteTask, getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, GripVertical, Trash2, Edit, Calendar, User, CheckCircle2, Clock, Circle } from 'lucide-react';

const COLUMNS = [
  { id: 'pendiente', label: 'Pendiente', icon: Circle, color: 'border-t-stone-400', bg: 'bg-stone-50' },
  { id: 'en_progreso', label: 'En Progreso', icon: Clock, color: 'border-t-blue-500', bg: 'bg-blue-50/50' },
  { id: 'completado', label: 'Completado', icon: CheckCircle2, color: 'border-t-emerald-500', bg: 'bg-emerald-50/50' },
];

const PRIORITIES = [
  { id: 'baja', label: 'Baja', color: 'bg-stone-100 text-stone-700' },
  { id: 'media', label: 'Media', color: 'bg-blue-100 text-blue-700' },
  { id: 'alta', label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  { id: 'urgente', label: 'Urgente', color: 'bg-red-100 text-red-700' },
];

export default function Tasks() {
  const { hasPermission, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({
    titulo: '', descripcion: '', estado: 'pendiente', prioridad: 'media',
    asignado_a: '', fecha_limite: '', etiquetas: []
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([getTasks(), getUsers()]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.titulo) return toast.error('El título es requerido');
    try {
      await createTask(form);
      toast.success('Tarea creada');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleUpdate = async () => {
    try {
      await updateTask(editTask.id, form);
      toast.success('Tarea actualizada');
      setEditTask(null);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleMove = async (taskId, newEstado) => {
    try {
      await moveTask(taskId, newEstado);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, estado: newEstado } : t));
    } catch (err) { toast.error('Error al mover tarea'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await deleteTask(id);
      toast.success('Tarea eliminada');
      fetchData();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setForm({
      titulo: task.titulo, descripcion: task.descripcion || '', estado: task.estado,
      prioridad: task.prioridad || 'media', asignado_a: task.asignado_a || '',
      fecha_limite: task.fecha_limite || '', etiquetas: task.etiquetas || []
    });
  };

  const resetForm = () => setForm({
    titulo: '', descripcion: '', estado: 'pendiente', prioridad: 'media',
    asignado_a: '', fecha_limite: '', etiquetas: []
  });

  if (!hasPermission('tareas', 'ver')) {
    return <div className="text-center py-20 text-stone-500">Acceso denegado</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Tareas</h1>
          <p className="text-stone-500 text-sm">{tasks.length} tareas totales</p>
        </div>
        {hasPermission('tareas', 'crear') && (
          <Button onClick={() => { setShowCreate(true); resetForm(); }} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Tarea
          </Button>
        )}
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map(col => {
          const colTasks = tasks.filter(t => t.estado === col.id);
          const Icon = col.icon;
          return (
            <div key={col.id} className={`rounded-lg border-t-4 ${col.color} ${col.bg} p-4 min-h-[400px]`} data-testid={`kanban-col-${col.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-stone-600" />
                  <h3 className="font-semibold text-stone-700">{col.label}</h3>
                </div>
                <Badge variant="secondary">{colTasks.length}</Badge>
              </div>
              <div className="space-y-3">
                {colTasks.map(task => (
                  <Card key={task.id} className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid={`task-card-${task.id}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <h4 className="font-medium text-sm text-stone-800 leading-tight">{task.titulo}</h4>
                        <div className="flex gap-1 shrink-0 ml-2">
                          {hasPermission('tareas', 'editar') && (
                            <button onClick={() => openEdit(task)} className="text-stone-400 hover:text-stone-700" data-testid={`edit-task-${task.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {hasPermission('tareas', 'eliminar') && (
                            <button onClick={() => handleDelete(task.id)} className="text-stone-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {task.descripcion && (
                        <p className="text-xs text-stone-500 line-clamp-2">{task.descripcion}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <Badge className={`text-xs ${PRIORITIES.find(p => p.id === task.prioridad)?.color || ''}`}>
                          {PRIORITIES.find(p => p.id === task.prioridad)?.label || task.prioridad}
                        </Badge>
                        {task.fecha_limite && (
                          <span className="text-xs text-stone-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />{task.fecha_limite.slice(0, 10)}
                          </span>
                        )}
                      </div>
                      {task.asignado_nombre && (
                        <div className="flex items-center gap-1.5">
                          <Avatar className="w-5 h-5">
                            <AvatarImage src={task.asignado_avatar} />
                            <AvatarFallback className="text-[9px] bg-stone-200">{task.asignado_nombre?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-stone-500">{task.asignado_nombre}</span>
                        </div>
                      )}
                      {/* Quick move buttons */}
                      <div className="flex gap-1 pt-1">
                        {COLUMNS.filter(c => c.id !== col.id).map(target => (
                          <button
                            key={target.id}
                            onClick={() => handleMove(task.id, target.id)}
                            className="text-[10px] px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200 text-stone-600"
                            data-testid={`move-${task.id}-to-${target.id}`}
                          >
                            {target.label}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate || !!editTask} onOpenChange={() => { setShowCreate(false); setEditTask(null); resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTask ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} data-testid="task-titulo" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={v => setForm({...form, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLUMNS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={v => setForm({...form, prioridad: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asignar a</Label>
                <Select value={form.asignado_a || 'none'} onValueChange={v => setForm({...form, asignado_a: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="task-asignado"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha límite</Label>
                <Input type="date" value={form.fecha_limite?.slice(0, 10) || ''} onChange={e => setForm({...form, fecha_limite: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditTask(null); }}>Cancelar</Button>
            <Button onClick={editTask ? handleUpdate : handleCreate} data-testid="submit-task-btn">
              {editTask ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
